import * as THREE from 'three';
import { isPointInRegion, type Region } from '../utils/geometry';

/**
 * Only r/g/b are used. Alpha is deliberately not a 4th data channel here —
 * see the class doc comment for why that combination doesn't work.
 */
export type SplatChannel = 'r' | 'g' | 'b';

const CHANNEL_OFFSET: Record<SplatChannel, number> = { r: 0, g: 1, b: 2 };

export interface SplatMapBounds {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

/**
 * Bakes course region data (fairway/green/bunker polygons and circles) into
 * an RGB texture whose channels are per-surface blend weights, sampled by
 * MaterialLibrary.terrainMaterial()'s shader. FairwayRenderer/GreenRenderer/
 * BunkerRenderer each call paintRegion() for their surface's regions, in
 * ascending priority order (fairway, then green, then bunker); TerrainRenderer
 * builds one of these, hands it to each in that order, then calls
 * toTexture() once everyone's painted. Rough is the implicit remainder
 * wherever r+g+b < 1 — it never needs its own paint call.
 *
 * Why not a 4th channel (alpha) for dirt: every paint call here uses plain
 * source-over with alpha=1, so a *later* region correctly overwrites an
 * *earlier* one's R/G/B in overlapping areas (bunker painted over green
 * painted over fairway gives bunker priority, matching Hole.surfaceTypeAt's
 * own priority order) — but that same alpha=1 would also stomp a
 * hypothetical alpha-encoded "dirt weight" to 1 everywhere ANY region is
 * painted, corrupting fairway/green/bunker with unwanted dirt blending.
 * There's also no real data source for dirt yet (no Hole.dirt regions
 * exist). A real dirt channel needs its own texture (or a redesigned
 * encoding) — adding one later doesn't require touching this class's
 * existing r/g/b behavior.
 */
export class SplatMapBaker {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly resolution: number;
  private readonly bounds: SplatMapBounds;

  constructor(bounds: SplatMapBounds, resolution = 512) {
    this.bounds = bounds;
    this.resolution = resolution;
    this.canvas = document.createElement('canvas');
    this.canvas.width = resolution;
    this.canvas.height = resolution;

    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('SplatMapBaker: 2D canvas context unavailable');
    this.ctx = ctx;
    // Opaque black base: r=g=b=0 everywhere unpainted reads as "100% rough" (the shader's remainder layer).
    this.ctx.fillStyle = 'rgba(0, 0, 0, 1)';
    this.ctx.fillRect(0, 0, resolution, resolution);
  }

  private worldToPixel(x: number, z: number): { px: number; py: number } {
    const { minX, maxX, minZ, maxZ } = this.bounds;
    const u = (x - minX) / (maxX - minX);
    const v = (z - minZ) / (maxZ - minZ);
    return { px: u * this.resolution, py: v * this.resolution };
  }

  /**
   * Paints a region fully opaque into one channel, zeroing the other two
   * at that location (plain source-over, alpha=1) — call in ascending
   * priority order so higher-priority surfaces (e.g. bunker) are painted
   * after lower-priority ones (e.g. fairway) and correctly win overlaps.
   */
  paintRegion(region: Region, channel: SplatChannel): void {
    this.ctx.fillStyle = channelColor(channel);
    this.ctx.beginPath();

    if (region.kind === 'circle') {
      const { px, py } = this.worldToPixel(region.center.x, region.center.z);
      const scaleX = this.resolution / (this.bounds.maxX - this.bounds.minX);
      this.ctx.ellipse(px, py, region.radius * scaleX, region.radius * scaleX, 0, 0, Math.PI * 2);
    } else {
      region.points.forEach((point, i) => {
        const { px, py } = this.worldToPixel(point.x, point.z);
        if (i === 0) this.ctx.moveTo(px, py);
        else this.ctx.lineTo(px, py);
      });
      this.ctx.closePath();
    }
    this.ctx.fill();
  }

  /** Point-accurate check for a world point against a region — exposed for tests, not used by the canvas paint path. */
  static isWeighted(point: { x: number; z: number }, region: Region): boolean {
    return isPointInRegion(point, region);
  }

  /** Applies a soft blur to feather all painted region edges, then returns the finished texture. */
  toTexture(featherPixels = 6): THREE.CanvasTexture {
    if (featherPixels > 0 && 'filter' in this.ctx) {
      const blurred = document.createElement('canvas');
      blurred.width = this.resolution;
      blurred.height = this.resolution;
      const blurredCtx = blurred.getContext('2d');
      if (blurredCtx) {
        blurredCtx.filter = `blur(${featherPixels}px)`;
        blurredCtx.drawImage(this.canvas, 0, 0);
        const texture = new THREE.CanvasTexture(blurred);
        texture.colorSpace = THREE.NoColorSpace; // this encodes raw blend weights, not color
        texture.needsUpdate = true;
        return texture;
      }
    }

    const texture = new THREE.CanvasTexture(this.canvas);
    texture.colorSpace = THREE.NoColorSpace;
    texture.needsUpdate = true;
    return texture;
  }
}

function channelColor(channel: SplatChannel): string {
  const rgb = [0, 0, 0];
  rgb[CHANNEL_OFFSET[channel]] = 255;
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 1)`;
}
